import { Slot } from '@radix-ui/react-slot';
import React from 'react';
import { tv } from 'tailwind-variants';
import cn from '@/utils/cn';

// AIDEV-NOTE: The SPA's shared button. Reach for this instead of hand-rolling a class string —
// hand-rolled buttons are how the SPA ended up with three different disabled opacities (40/50/60)
// and two different disabled cursors across six components.
//
// Two rules baked in here deliberately:
//   1. Disabled uses flat tokens, never opacity. Opacity is relative, so the same value lands
//      differently on every fill, and it fades the label along with the background (white on a
//      50%-faded purple measured 2.69:1 in light mode).
//   2. Filled buttons use accent-solid, never accent. White on dark --spa-accent (#a855f7) is
//      3.96:1 and fails AA; accent is for borders, rings and focus outlines only.
//
// Focus is an outline rather than a ring so it doesn't need to know the surface colour it sits on
// (a ring-offset would have to match bg vs bg-secondary per consumer).
const buttonVariants = tv({
  base: [
    'tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5',
    'tw-font-medium tw-leading-5 tw-box-border tw-cursor-pointer tw-transition-colors',
    // Inert on buttons; keeps the global anchor underline off asChild links.
    'tw-no-underline hover:tw-no-underline focus:tw-no-underline',
    'disabled:tw-cursor-not-allowed',
    'disabled:tw-bg-spa-bg-disabled disabled:tw-text-spa-disabled disabled:tw-border-0',
    'focus-visible:tw-outline focus-visible:tw-outline-2',
    'focus-visible:tw-outline-spa-accent focus-visible:tw-outline-offset-2',
  ],
  defaultVariants: { size: 'sm', variant: 'secondary' },
  variants: {
    size: {
      md: 'tw-px-4 tw-py-2 tw-text-sm tw-rounded',
      sm: 'tw-px-3.5 tw-py-1.5 tw-text-sm tw-rounded-md',
      xs: 'tw-px-3 tw-py-1.5 tw-text-xs tw-rounded',
    },
    variant: {
      danger: [
        'tw-border-0 tw-bg-spa-danger-solid',
        'tw-text-white hover:tw-text-white hover:tw-bg-spa-danger-solid-hover',
      ],
      'danger-outline': [
        'tw-border tw-border-solid tw-border-spa-danger-solid tw-bg-spa-bg-secondary',
        'tw-text-spa-error hover:tw-bg-spa-danger-solid hover:tw-text-white',
      ],
      outline: [
        'tw-border tw-border-solid tw-border-spa-accent tw-bg-spa-bg-secondary',
        'tw-text-spa-accent-text hover:tw-bg-spa-accent-bg-subtle',
      ],
      primary: [
        'tw-border-0 tw-bg-spa-accent-solid',
        'tw-text-white hover:tw-text-white hover:tw-bg-spa-accent-solid-hover',
      ],
      // Surface-filled so it reads as a raised control on the page ground and stays flat on a
      // surface. Hover is bg-tertiary, not bg-hover: bg-hover is the subtler row/item tint.
      secondary: [
        'tw-border tw-border-solid tw-border-spa-border tw-bg-spa-bg-secondary',
        'tw-text-spa-primary hover:tw-bg-spa-bg-tertiary hover:tw-text-spa-heading',
      ],
    },
  },
});

export type SPAButtonSize = 'xs' | 'sm' | 'md';
export type SPAButtonVariant = 'danger' | 'danger-outline' | 'outline' | 'primary' | 'secondary';

interface SPAButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Render the single child element (e.g. an <a> or router <Link>) with button styling. */
  asChild?: boolean;
  size?: SPAButtonSize;
  variant?: SPAButtonVariant;
}

const SPAButton = React.forwardRef<HTMLButtonElement, SPAButtonProps>(
  ({ asChild = false, className, size, type = 'button', variant, ...props }, ref) => {
    const classes = cn(buttonVariants({ size, variant }), className);

    if (asChild) {
      return <Slot ref={ref as React.Ref<HTMLElement>} className={classes} {...props} />;
    }

    return (
      <button
        ref={ref}
        className={classes}
        // eslint-disable-next-line react/button-has-type
        type={type}
        {...props}
      />
    );
  },
);

SPAButton.displayName = 'SPAButton';

export default SPAButton;
